// Classic arcade titles surfaced on the Flarecade homepage.
//
// This catalog is reconciled to the canonical license-safe list at
// ~/Documents/Adam Lapins/Working/Flarecade/Open-Source-Games-GoodToGo.md, which
// was recovered from the 2026-09-12 licensing audit (session f6bd9ba6). Every
// title here has a permissive-licensed open-source implementation cleared for
// porting (MIT / BSD / MPL). Trademarked titles ship under original Flarecade
// names with original art — never the trademarked name or assets.
//
// Ordering is fixed by Adam: Freedoom, then 2048, then Breakout, then remaining
// titles by franchise all-time popularity.
//
// Only titles with `playable` true have a live route today (`href`); every
// other title renders a "Coming Soon" overlay. `accent` drives scroll theming
// when a classic row is in view. `hardware` names the original controller for
// the in-play controller overlay. `cloneOf` is reference only and is never
// rendered as the shipped title.

export interface ClassicDefinition {
  readonly id: string;
  readonly title: string;
  readonly cloneOf?: string;
  readonly year: number;
  readonly genre: string;
  readonly license: string;
  readonly accent: string;
  readonly hardware: string;
  readonly playable: boolean;
  readonly href?: string;
}

export const classicsCatalog: readonly ClassicDefinition[] = [
  {
    id: "doom",
    title: "Freedoom",
    cloneOf: "Doom",
    year: 1993,
    genre: "FPS",
    license: "Freedoom (BSD assets)",
    accent: "#ff5d5d",
    hardware: "PC keyboard + mouse (1993)",
    playable: true,
    href: "/arcade-classics/freedoom/",
  },
  {
    id: "2048",
    title: "2048",
    year: 2014,
    genre: "Puzzle",
    license: "MIT",
    accent: "#f6c945",
    hardware: "Touch swipe / arrow keys (2014)",
    playable: true,
    href: "/arcade-classics/2048/",
  },
  {
    id: "breakout",
    title: "Brick Blitz",
    cloneOf: "Breakout",
    year: 1976,
    genre: "Brick breaker",
    license: "MIT",
    accent: "#42e2c3",
    hardware: "Atari paddle (1976)",
    playable: true,
    href: "/arcade-classics/brick-blitz/",
  },
  {
    id: "snake",
    title: "Serpent Clash",
    cloneOf: "Snake",
    year: 1997,
    genre: "Arcade",
    license: "MIT",
    accent: "#5be36b",
    hardware: "Phone D-pad (1997)",
    playable: true,
    href: "/arcade-classics/serpent-clash/",
  },
  {
    id: "asteroids",
    title: "Asteroid Storm",
    cloneOf: "Asteroids",
    year: 1979,
    genre: "Vector shooter",
    license: "MIT",
    accent: "#9fd8ff",
    hardware: "Arcade 5-button (1979)",
    playable: true,
    href: "/arcade-classics/asteroid-storm/",
  },
  {
    id: "space-invaders",
    title: "Invader Defense",
    cloneOf: "Space Invaders",
    year: 1978,
    genre: "Fixed shooter",
    license: "MIT",
    accent: "#2fe3ff",
    hardware: "Arcade 2-way + fire (1978)",
    playable: true,
    href: "/arcade-classics/invader-defense/",
  },
  {
    id: "pong",
    title: "Paddle Duel",
    cloneOf: "Pong",
    year: 1972,
    genre: "Paddle",
    license: "MIT",
    accent: "#e6e6e6",
    hardware: "Atari paddle (1972)",
    playable: true,
    href: "/arcade-classics/paddle-duel/",
  },
  {
    id: "pac-man",
    title: "Pellet Pursuit",
    cloneOf: "Pac-Man",
    year: 1980,
    genre: "Maze",
    license: "MIT",
    accent: "#ffd23f",
    hardware: "Arcade 4-way joystick (1980)",
    playable: true,
    href: "/arcade-classics/pellet-pursuit/",
  },
  {
    id: "galaga",
    title: "Galactic Swarm",
    cloneOf: "Galaga",
    year: 1981,
    genre: "Fixed shooter",
    license: "MIT",
    accent: "#4fb0ff",
    hardware: "Arcade 2-way + fire (1981)",
    playable: true,
    href: "/arcade-classics/galactic-swarm/",
  },
  {
    id: "frogger",
    title: "Traffic Dodger",
    cloneOf: "Frogger",
    year: 1981,
    genre: "Action",
    license: "MIT",
    accent: "#7bd66b",
    hardware: "Arcade 4-way joystick (1981)",
    playable: true,
    href: "/arcade-classics/traffic-dodger/",
  },
  {
    id: "hextris",
    title: "Hextris",
    year: 2014,
    genre: "Puzzle",
    license: "MIT",
    accent: "#8b72ff",
    hardware: "Touch / arrow keys (2014)",
    playable: true,
    href: "/arcade-classics/hextris/",
  },
  {
    id: "skifree",
    title: "Slope Escape",
    cloneOf: "SkiFree",
    year: 1991,
    genre: "Action",
    license: "MIT",
    accent: "#bfe9ff",
    hardware: "PC keyboard (1991)",
    playable: true,
    href: "/arcade-classics/slope-escape/",
  },
  {
    id: "duck-hunt",
    title: "Clay Shoot",
    cloneOf: "Duck Hunt",
    year: 1984,
    genre: "Light-gun",
    license: "MIT",
    accent: "#ff9f1c",
    hardware: "NES Zapper (1984)",
    playable: true,
    href: "/arcade-classics/clay-shoot/",
  },
  {
    id: "sokoban",
    title: "Crate Pusher",
    cloneOf: "Sokoban",
    year: 1982,
    genre: "Puzzle",
    license: "MIT",
    accent: "#d4a373",
    hardware: "Arcade 4-way joystick (1982)",
    playable: true,
    href: "/arcade-classics/crate-pusher/",
  },
  {
    id: "minesweeper",
    title: "Mine Sweep",
    cloneOf: "Minesweeper",
    year: 1989,
    genre: "Puzzle",
    license: "BSD-3-Clause",
    accent: "#c0c0c0",
    hardware: "Mouse (1989)",
    playable: true,
    href: "/arcade-classics/mine-sweep/",
  },
  {
    id: "tempest",
    title: "Ring Runner",
    cloneOf: "Tempest",
    year: 1981,
    genre: "Tube shooter",
    license: "MIT",
    accent: "#e75bc4",
    hardware: "Arcade spinner + fire (1981)",
    playable: true,
    href: "/arcade-classics/ring-runner/",
  },
  {
    id: "qix",
    title: "Grid Claim",
    cloneOf: "Qix / Xonix",
    year: 1981,
    genre: "Territory",
    license: "MIT",
    accent: "#00d1b2",
    hardware: "Arcade 4-way joystick (1981)",
    playable: true,
    href: "/arcade-classics/grid-claim/",
  },
  {
    id: "hexgl",
    title: "Velocity Prime",
    cloneOf: "F-Zero / Wipeout",
    year: 2012,
    genre: "Racing",
    license: "MIT",
    accent: "#1ec8ff",
    hardware: "Keyboard / gamepad (2012)",
    playable: true,
    href: "/arcade-classics/velocity-prime/",
  },
  {
    id: "outrun",
    title: "Sunset Sprint",
    cloneOf: "OutRun",
    year: 1986,
    genre: "Racing",
    license: "MIT",
    accent: "#ff7a1a",
    hardware: "Arcade wheel + pedals (1986)",
    playable: true,
    href: "/arcade-classics/sunset-sprint/",
  },
  {
    id: "kurve",
    title: "Curve Clash",
    cloneOf: "Achtung die Kurve",
    year: 1995,
    genre: "Multiplayer",
    license: "MIT",
    accent: "#f15bb5",
    hardware: "Shared keyboard (1995)",
    playable: true,
    href: "/arcade-classics/curve-clash/",
  },
  {
    id: "connect-four",
    title: "Four Up",
    cloneOf: "Connect Four",
    year: 1974,
    genre: "Strategy",
    license: "MIT",
    accent: "#ffd23f",
    hardware: "Mouse / touch (1974)",
    playable: true,
    href: "/arcade-classics/four-up/",
  },
  {
    id: "mahjong",
    title: "Tile Match",
    cloneOf: "Mahjong Solitaire",
    year: 1986,
    genre: "Tile matching",
    license: "MIT",
    accent: "#43aa8b",
    hardware: "Mouse / touch (1986)",
    playable: true,
    href: "/arcade-classics/tile-match/",
  },
  {
    id: "digger",
    title: "Gem Digger",
    cloneOf: "Digger / Boulder Dash",
    year: 1983,
    genre: "Dig-and-collect",
    license: "MIT",
    accent: "#e0a458",
    hardware: "Arcade 4-way joystick (1983)",
    playable: true,
    href: "/arcade-classics/gem-digger/",
  },
  {
    id: "breakout-vs",
    title: "Brick Blitz VS",
    cloneOf: "Breakout (multiplayer)",
    year: 1976,
    genre: "Brick breaker",
    license: "MIT",
    accent: "#4cc9f0",
    hardware: "Atari paddle x2 (1976)",
    playable: true,
    href: "/arcade-classics/brick-blitz-vs/",
  },
  {
    id: "missile-command",
    title: "Missile Guard",
    cloneOf: "Missile Command",
    year: 1980,
    genre: "Fixed shooter",
    license: "MIT",
    accent: "#f94144",
    hardware: "Arcade trackball + fire (1980)",
    playable: true,
    href: "/arcade-classics/missile-guard/",
  },
  {
    id: "arkanoid",
    title: "Brick Bounce",
    cloneOf: "Arkanoid",
    year: 1986,
    genre: "Brick breaker",
    license: "MIT",
    accent: "#90be6d",
    hardware: "Arcade spinner (1986)",
    playable: true,
    href: "/arcade-classics/brick-bounce/",
  },
  {
    id: "a-dark-room",
    title: "The Dark Room",
    cloneOf: "A Dark Room",
    year: 2013,
    genre: "Text adventure",
    license: "MPL-2.0",
    accent: "#adb5bd",
    hardware: "Mouse / keyboard (2013)",
    playable: true,
    href: "/arcade-classics/the-dark-room/",
  },
  {
    id: "rogue",
    title: "Deep Rogue",
    cloneOf: "Rogue",
    year: 1980,
    genre: "Roguelike",
    license: "BSD",
    accent: "#b5838d",
    hardware: "PC keyboard (1980)",
    playable: false,
  },
  {
    id: "openlara",
    title: "Relic Raider",
    cloneOf: "Tomb Raider (OpenLara)",
    year: 1996,
    genre: "Action-adventure",
    license: "BSD-2-Clause",
    accent: "#c9a227",
    hardware: "Keyboard / gamepad (1996)",
    playable: false,
  },
  {
    id: "mario",
    title: "Plumber Quest",
    cloneOf: "Super Mario Bros",
    year: 1985,
    genre: "Platformer",
    license: "MIT",
    accent: "#e8402e",
    hardware: "NES pad (1985)",
    playable: false,
  },
];

export function isClassicPlayable(def: ClassicDefinition): boolean {
  return def.playable && typeof def.href === "string" && def.href.length > 0;
}
